import { useContext, useEffect, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Contribution,
  ContributionCollection,
} from "wealthmode-frontend/backend"
import { Text } from "wealthmode-frontend/gluestack/ui"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { DetailField, ResourceDetail } from "@/components"
import { BackendContext } from "@/context"
import { contributionAmount, contributionTotal } from "@/lib/contributions"
import { CONTRIBUTION_KIND_LABELS, contributionLimitKindLabel } from "@/lib/labels"

export default function ContributionDetail() {
  const { client_id, account_id, contribution_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
    contribution_id: string
  }>()

  const backend = useContext(BackendContext)

  const [contribution, setContribution] = useState<Contribution>()

  useEffect(() => {
    ;(async () => {
      const clients = backend.collection("clients") as ClientCollection
      const client = clients.model(client_id)
      await client.fetch({ reload: true })

      const accounts = client.link("accounts")?.resolve() as AccountCollection
      const account = accounts.model(account_id)
      await account.fetch({ reload: true })

      const data = account.embed("data") as AccountData
      const contributions = data.link("contributions")?.resolve() as ContributionCollection | undefined
      if (!contributions) {
        return
      }

      const model = contributions.model(contribution_id)
      await model.fetch({ reload: true })

      setContribution(model)
    })().catch((err) => {
      console.error("Error: " + err)
      throw err
    })
  }, [backend, client_id, account_id, contribution_id])

  const fields: DetailField[] = []

  if (contribution) {
    const { covered_from, covered_through } = contribution.attributes
    const sources = contribution.sources()

    fields.push(
      { label: "Tax Year", value: contribution.attributes.tax_year },
      {
        label: "Covered",
        value: covered_from == null || covered_through == null ? "--" : `${covered_from} – ${covered_through}`,
      },
      ...CONTRIBUTION_KIND_LABELS.map(({ kind, title }) => {
        const amount = contributionAmount(sources, kind)
        return { label: title, value: amount == null ? "--" : formatMoneyFull(amount) }
      }),
      { label: "Total", value: formatMoneyFull(contributionTotal(sources)) },
      ...contribution.limits().map((limit) => ({
        label: `${contributionLimitKindLabel(limit.attributes.kind)} Limit`,
        value: (
          <Link href={`/contribution_limits/${limit.id}`}>
            <Text className="text-markdown-link hover:opacity-80 underline">
              {formatMoneyFull(contribution.countedToward(limit))} / {formatMoneyFull(limit.attributes.maximum)}
            </Text>
          </Link>
        ),
      })),
    )
  }

  return (
    <ResourceDetail
      title={contribution ? `${contribution.attributes.tax_year} Contributions` : "Contribution"}
      subtitle={contribution_id}
      loading={!contribution}
      fields={fields}
      links={[]}
    />
  )
}
